import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useOrganization } from "@/hooks/useOrganization";
import { usePlayerCredits } from "@/hooks/usePlayerCredits";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { Wallet, Plus, ArrowUpRight, ArrowDownRight } from "lucide-react";
import { formatPrice, resolveMoney } from "@/utils/formatPrice";

// Sprint D Plan A — dual-state types.
interface CreditPackage {
  id: string;
  name: string;
  price: number;                       // legacy kronor
  price_minor: number | null;          // öre
  credit_value: number;                // legacy kronor
  credit_value_minor: number | null;
  currency: string;
  is_active: boolean;
  sort_order: number | null;
}

interface CreditTransaction {
  id: string;
  amount: number;                      // legacy kronor
  amount_minor: number | null;
  currency: string;
  type: string;
  description: string | null;
  source: string | null;
  created_at: string;
}

const typeLabels: Record<string, string> = {
  purchase: "Köp",
  booking_payment: "Bokning",
  refund: "Återbetalning",
  admin_adjustment: "Justering",
};

const SOURCE_LABELS: Record<string, string> = {
  manual: "Manuell",
  kassan: "Kassan",
  epassi: "ePassi",
  wellnet: "Wellnet",
  actiway: "Actiway",
  stripe: "Kort",
  purchase: "Köp",
  refund: "Återbetalning",
  system: "System",
};

export default function PlayerCreditsSection() {
  const { user } = useAuth();
  const { organization } = useOrganization();
  const { balance, transactions } = usePlayerCredits();
  const qc = useQueryClient();

  const { data: packages = [] } = useQuery({
    queryKey: ["credit-packages", organization?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("credit_packages" as any)
        .select("*")
        .eq("organization_id", organization!.id)
        .eq("is_active", true)
        .order("sort_order");
      if (error) throw error;
      return (data as CreditPackage[] | null) ?? [];
    },
    enabled: !!organization?.id,
  });

  const purchaseMutation = useMutation({
    mutationFn: async (pkg: CreditPackage) => {
      if (!user || !organization) throw new Error("Ej inloggad");

      // Sprint D dual-state: prefer pkg._minor; fall back to legacy.
      const priceMoney = resolveMoney(pkg.price_minor, pkg.price, pkg.currency);
      const valueMoney = resolveMoney(pkg.credit_value_minor, pkg.credit_value, pkg.currency);
      if (!priceMoney || !valueMoney) throw new Error("Paket saknar pris/värde");
      const priceKronor = Math.round(priceMoney.valueMinor / 100);
      const valueKronor = Math.round(valueMoney.valueMinor / 100);

      // For now: direct purchase (Stripe integration can be added later)
      // Create checkout session via edge function
      const baseUrl = window.location.origin;
      try {
        const { data: checkoutData, error: checkoutError } = await supabase.functions.invoke(
          "create-checkout-session",
          {
            body: {
              amount: priceKronor,
              success_url: `${baseUrl}/profile?credit_purchase=success&package_id=${pkg.id}`,
              cancel_url: `${baseUrl}/profile`,
              metadata: {
                type: "credit_purchase",
                package_id: pkg.id,
                credit_value: valueKronor,
                user_id: user.id,
                organization_id: organization.id,
              },
            },
          }
        );
        if (checkoutError) throw checkoutError;
        if (checkoutData?.url) {
          window.location.href = checkoutData.url;
          return;
        }
      } catch {
        // Fallback: manual credit addition (for demo/testing)
      }

      // Fallback: direct credit addition (dual-write legacy + _minor)
      const { data: existing } = await supabase
        .from("player_credits" as any)
        .select("balance, balance_minor, currency")
        .eq("user_id", user.id)
        .eq("organization_id", organization.id)
        .maybeSingle();

      const currentMoney = resolveMoney(
        (existing as { balance_minor?: number | null } | null)?.balance_minor,
        (existing as { balance?: number } | null)?.balance,
        (existing as { currency?: string } | null)?.currency,
      );
      const newBalanceMinor = (currentMoney?.valueMinor ?? 0) + valueMoney.valueMinor;
      const newBalanceKronor = Math.round(newBalanceMinor / 100);

      if (existing) {
        await supabase
          .from("player_credits" as any)
          .update({
            balance: newBalanceKronor,
            balance_minor: newBalanceMinor,
            currency: "SEK",
            updated_at: new Date().toISOString(),
          })
          .eq("user_id", user.id)
          .eq("organization_id", organization.id);
      } else {
        await supabase
          .from("player_credits" as any)
          .insert({
            user_id: user.id,
            organization_id: organization.id,
            balance: newBalanceKronor,
            balance_minor: newBalanceMinor,
            currency: "SEK",
          });
      }

      await supabase.from("credit_transactions" as any).insert({
        user_id: user.id,
        organization_id: organization.id,
        amount: valueKronor,                           // legacy kronor
        amount_minor: valueMoney.valueMinor,           // öre
        currency: "SEK",
        type: "purchase",
        reference_id: pkg.id,
        description: `Köp: ${pkg.name}`,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["player-credits"] });
      qc.invalidateQueries({ queryKey: ["credit-transactions"] });
       toast.success("Klippkort påfyllt! 🎉");
    },
    onError: (err: Error) => { console.error(err); toast.error(err?.message || "Kunde inte köpa klippkort"); },
  });

  return (
    <div className="space-y-4">
      {/* Balance */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Wallet className="h-5 w-5 text-primary" />
              <span className="text-sm text-muted-foreground">Mitt klippkortssaldo</span>
            </div>
            <span className="text-2xl font-bold">{formatPrice(balance)}</span>
          </div>
        </CardContent>
      </Card>

      {/* Packages */}
      {packages.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold flex items-center gap-1.5">
            <Plus className="h-4 w-4" /> Fyll på klippkort
          </h3>
          <div className="grid gap-2 sm:grid-cols-2">
            {packages.map((pkg) => {
              // Sprint D dual-state: prefer _minor.
              const priceMoney = resolveMoney(pkg.price_minor, pkg.price, pkg.currency);
              const valueMoney = resolveMoney(pkg.credit_value_minor, pkg.credit_value, pkg.currency);
              const bonusMinor = (valueMoney?.valueMinor ?? 0) - (priceMoney?.valueMinor ?? 0);
              return (
                <Card key={pkg.id} className="overflow-hidden">
                  <CardContent className="p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm">{pkg.name}</span>
                      {bonusMinor > 0 && (
                        <Badge variant="default" className="text-xs">
                          +{formatPrice({ valueMinor: bonusMinor, currency: "SEK" })} bonus
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-lg font-bold">{formatPrice(priceMoney ?? { valueMinor: 0, currency: "SEK" })}</span>
                      <span className="text-sm text-muted-foreground">→ {formatPrice(valueMoney ?? { valueMinor: 0, currency: "SEK" })} klippkort</span>
                    </div>
                    <Button
                      size="sm"
                      className="w-full"
                      onClick={() => purchaseMutation.mutate(pkg)}
                      disabled={purchaseMutation.isPending}
                    >
                      Köp
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Transaction history */}
      {transactions.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold">Klippkortshistorik</h3>
          <div className="border rounded-lg divide-y max-h-64 overflow-y-auto">
            {transactions.map((tx: CreditTransaction) => {
              // Sprint D dual-state: prefer amount_minor.
              const txMoney = resolveMoney(tx.amount_minor, tx.amount, tx.currency);
              const txMinor = txMoney?.valueMinor ?? 0;
              return (
              <div key={tx.id} className="flex items-center justify-between px-3 py-2 text-sm">
                <div className="flex items-center gap-2">
                  {txMinor > 0 ? (
                    <ArrowUpRight className="h-3.5 w-3.5 text-emerald-500" />
                  ) : (
                    <ArrowDownRight className="h-3.5 w-3.5 text-destructive" />
                  )}
                  <span className="text-muted-foreground">{typeLabels[tx.type] ?? tx.type}</span>
                  {tx.description && <span className="text-xs text-muted-foreground truncate max-w-32">— {tx.description}</span>}
                  {tx.source && tx.source !== "manual" && (
                    <Badge variant="outline" className="text-[10px] shrink-0">{SOURCE_LABELS[tx.source] ?? tx.source}</Badge>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`font-medium ${txMinor > 0 ? "text-emerald-600 dark:text-emerald-400" : ""}`}>
                    {txMinor > 0 ? "+" : ""}{formatPrice(txMoney ?? { valueMinor: 0, currency: "SEK" })}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {format(new Date(tx.created_at), "d MMM", { locale: sv })}
                  </span>
                </div>
              </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
